import { ReactNode, useRef, useState } from "react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useTranslation } from "react-i18next";
import { handleReviewError } from "./errorHandling";

const isDev = process.env.NODE_ENV !== "production";

/**
 * Scoped react-query provider for the Flyer Review feature ONLY.
 *
 * A fresh `QueryClient` is created per mount (stable via `useState`). It is
 * intentionally NOT mounted in `App.tsx`: no other page uses react-query, so
 * keeping the provider here guarantees the rest of the app is unaffected.
 *
 * Unhandled query/mutation errors are funneled to the existing
 * `handleReviewError` toast (preserving current error UX). A query/mutation
 * can set `meta: { suppressGlobalError: true }` to render its own inline
 * error instead of (or in addition to a deliberately suppressed) toast.
 */
export function ReviewQueryProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;

  const [client] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error, query) => {
            if (query.meta?.suppressGlobalError) return;
            void handleReviewError(error, tRef.current);
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, _vars, _ctx, mutation) => {
            if (mutation.meta?.suppressGlobalError) return;
            void handleReviewError(error, tRef.current);
          },
        }),
        defaultOptions: {
          queries: {
            retry: 1,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={client}>
      {children}
      {isDev ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </QueryClientProvider>
  );
}
